"use client";

import Link from "next/link";
import {
  Smartphone,
  Globe,
  Check,
  MapPin,
  Map,
  UtensilsCrossed,
  Star,
  RefreshCw,
  Heart,
  Store,
  ArrowRight,
} from "lucide-react";
import { AppStoreDownloadButtons } from "@/components/marketing/AppStoreDownloadButtons";
import { MarketingFooter } from "@/components/marketing/MarketingFooter";
import { MarketingHeader } from "@/components/marketing/MarketingHeader";
import { MarketingHeroSecondaryLinks } from "@/components/marketing/MarketingHeroSecondaryLinks";
import { MarketingTrustPillRows } from "@/components/marketing/MarketingTrustPillRows";
import { IPhoneMockup } from "@/components/IPhoneMockup";
import { useEffect, useRef } from "react";
import { useLanguage } from "@/lib/useLanguage";
import { useDevice } from "@/lib/useDevice";
import { getAppStoreUrls, appendQueryParams } from "@/lib/app-store-urls";
import { capturePostHogEvent } from "@/lib/posthog-client";
import {
  POSTHOG_EVENTS,
  POSTHOG_SOURCE,
} from "@/lib/posthog-events";
import type { Language } from "@/lib/translations";

const SCHEME_PREFIX = "menuhunter://";

/** If the app does not take focus (custom scheme unhandled), send mobile users to the store. */
const OPEN_IN_APP_STORE_FALLBACK_MS = 1800;

/** Locale-safe screenshot suffix; invalid values fall back to Spanish. */
function resolveScanScreenshotLanguage(lang: unknown): Language {
  if (lang === "es" || lang === "en" || lang === "ca") return lang;
  return "es";
}

function getAppDeepLinkForTarget(targetPath: string): string | null {
  const trimmed = targetPath.replace(/^\/+/, "").replace(/\/+$/, "");
  if (/^m\/[a-zA-Z0-9_-]+$/.test(trimmed)) return `${SCHEME_PREFIX}/${trimmed}`;
  if (/^l\/[a-zA-Z0-9_-]+$/.test(trimmed)) return `${SCHEME_PREFIX}/${trimmed}`;
  if (/^lists\/join\/[a-zA-Z0-9_-]+$/.test(trimmed)) return `${SCHEME_PREFIX}/${trimmed}`;
  return null;
}

function getContinueOnWebHref(targetPath: string): string | null {
  if (/^\/m\/[a-zA-Z0-9_-]+$/.test(targetPath)) return targetPath;
  if (/^\/l\/[a-zA-Z0-9_-]+$/.test(targetPath)) return targetPath;
  if (/^\/lists\/join\/[a-zA-Z0-9_-]+$/.test(targetPath)) return targetPath;
  return null;
}

export type ScanDownloadPageContentProps = {
  source: "scan" | "download";
  preserveParams?: URLSearchParams | null;
  targetPath?: string | null;
};

export function ScanDownloadPageContent({
  source,
  preserveParams = null,
  targetPath = null,
}: ScanDownloadPageContentProps) {
  const { language, t } = useLanguage();
  const device = useDevice();
  const screenshotLang = resolveScanScreenshotLanguage(language);
  const scanScreenshots = {
    map: `/images/scan/map-${screenshotLang}.jpg`,
    menu: `/images/scan/menu-${screenshotLang}.jpg`,
    home: `/images/scan/home-${screenshotLang}.jpg`,
  } as const;

  const { ios: iosUrl, android: androidUrl } = getAppStoreUrls();
  const iosHref = appendQueryParams(iosUrl, preserveParams);
  const androidHref = appendQueryParams(androidUrl, preserveParams);

  const appDeepLink = targetPath ? getAppDeepLinkForTarget(targetPath) : null;
  const continueHref = targetPath ? getContinueOnWebHref(targetPath) : null;
  const showTargetCtas = !!targetPath && (!!appDeepLink || !!continueHref);

  const openInAppFallbackCleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    return () => {
      openInAppFallbackCleanupRef.current?.();
      openInAppFallbackCleanupRef.current = null;
    };
  }, []);

  const handleOpenInApp = () => {
    if (!appDeepLink) return;

    openInAppFallbackCleanupRef.current?.();

    const storeUrl = device === "android" ? androidHref : iosHref;
    let settled = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const settle = () => {
      if (settled) return;
      settled = true;
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pagehide", onPageHide);
      openInAppFallbackCleanupRef.current = null;
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") settle();
    };

    const onPageHide = () => settle();

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pagehide", onPageHide);

    timeoutId = setTimeout(() => {
      timeoutId = null;
      if (settled) return;
      if (document.visibilityState === "visible") {
        window.location.replace(storeUrl);
      }
      settle();
    }, OPEN_IN_APP_STORE_FALLBACK_MS);

    openInAppFallbackCleanupRef.current = settle;

    if (device === "ios") {
      const iframe = document.createElement("iframe");
      iframe.setAttribute("style", "display:none;width:0;height:0;border:0;");
      iframe.setAttribute("aria-hidden", "true");
      iframe.src = appDeepLink;
      document.body.appendChild(iframe);
      setTimeout(() => {
        try {
          iframe.remove();
        } catch {
          /* ignore */
        }
      }, 2500);
    } else {
      window.location.href = appDeepLink;
    }
  };

  const handleAppStore = () => {
    capturePostHogEvent(POSTHOG_EVENTS.SCAN_CTA_APP_STORE, {
      source: POSTHOG_SOURCE,
      page: source,
      device_type: device,
    });
  };

  const handleGooglePlay = () => {
    capturePostHogEvent(POSTHOG_EVENTS.SCAN_CTA_GOOGLE_PLAY, {
      source: POSTHOG_SOURCE,
      page: source,
      device_type: device,
    });
  };

  const isDesktop = device !== "ios" && device !== "android";

  return (
    <div
      key={language}
      className="min-h-screen flex flex-col bg-white text-slate-900 dark:bg-slate-950 dark:text-slate-100"
    >
      <MarketingHeader locale={language} />

      <main className="flex-1">
        <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-10 sm:py-12 pb-16">
          {showTargetCtas && (
            <div className="mb-8 rounded-xl bg-slate-50 border border-slate-200 dark:bg-slate-800/60 dark:border-slate-700/60 p-5 sm:p-6 shadow-lg">
              <div className="flex flex-col gap-3">
                {!isDesktop ? (
                  <button
                    type="button"
                    onClick={handleOpenInApp}
                    className="flex items-center justify-center gap-2 w-full py-3.5 px-5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-semibold text-sm transition-all shadow-lg shadow-blue-900/30 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-slate-950"
                  >
                    <Smartphone className="w-5 h-5 shrink-0" aria-hidden />
                    {t.download.openInApp}
                  </button>
                ) : null}
                {continueHref ? (
                  <Link
                    href={continueHref}
                    className="flex items-center justify-center gap-2 w-full py-3 px-5 rounded-xl border border-slate-300 bg-white text-slate-700 hover:bg-slate-100 dark:border-slate-600 dark:bg-slate-800/40 dark:text-slate-200 dark:hover:bg-slate-700/50 font-medium text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-slate-950"
                  >
                    <Globe className="w-5 h-5 shrink-0" aria-hidden />
                    {t.download.continueOnWeb}
                  </Link>
                ) : (
                  <span className="flex items-center justify-center gap-2 w-full py-3 px-5 rounded-xl border border-slate-300 bg-white text-slate-500 dark:border-slate-600 dark:bg-slate-800/40 dark:text-slate-400 font-medium text-sm">
                    <Globe className="w-5 h-5 shrink-0" aria-hidden />
                    {t.download.continueOnWeb}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Hero */}
          <section className="mb-14 lg:mb-16 lg:pb-4">
            <div className="grid grid-cols-1 gap-10 lg:gap-12 items-center lg:items-start lg:grid-cols-[minmax(0,1fr)_minmax(536px,0.95fr)] xl:grid-cols-[minmax(0,1fr)_minmax(584px,0.95fr)]">
              <div className="min-w-0 text-center lg:text-left">
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 dark:text-white leading-[1.12] mb-4 max-w-xl mx-auto lg:mx-0">
                  {t.scan.hero.headline}
                </h1>
                <p className="text-base sm:text-lg text-slate-600 dark:text-slate-400 leading-relaxed mb-6 max-w-xl mx-auto lg:mx-0">
                  {t.scan.hero.subheadline}
                </p>
                {isDesktop && (
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3 max-w-xl mx-auto lg:mx-0 text-center lg:text-left" role="status">
                    {t.scan.hero.desktopHelper}
                  </p>
                )}
                <div className="flex flex-col gap-4 justify-center lg:justify-start max-w-md w-full mx-auto lg:mx-0">
                  <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                    {t.homePage.hero.downloadLabel}
                  </p>
                  <AppStoreDownloadButtons
                    iosHref={iosHref}
                    androidHref={androidHref}
                    iosLabel={t.scan.cta.appStore}
                    androidLabel={t.scan.cta.googlePlay}
                    onIosClick={handleAppStore}
                    onAndroidClick={handleGooglePlay}
                  />
                  <Link
                    href="/explore"
                    className="inline-flex items-center justify-center gap-2 w-full min-h-16 px-6 py-3 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800/40 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700/50 font-semibold text-base leading-snug transition-colors focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-slate-950"
                  >
                    {t.scan.hero.links.interactiveMap}
                    <MapPin className="w-4 h-4 shrink-0" aria-hidden />
                  </Link>
                  <MarketingHeroSecondaryLinks
                    links={[
                      {
                        href: `/${language}/menu-del-dia-barcelona`,
                        label: t.scan.hero.links.exploreMenus,
                        icon: <ArrowRight className="w-4 h-4 shrink-0" aria-hidden />,
                      },
                      {
                        href: "/restaurants",
                        label: t.scan.hero.links.restaurants,
                        icon: <Store className="w-4 h-4 shrink-0" aria-hidden />,
                      },
                    ]}
                  />
                </div>

                <MarketingTrustPillRows pills={t.homePage.trust.pills} />
              </div>
              <div className="min-w-0 w-full">
                <IPhoneMockup
                  src={scanScreenshots.map}
                  alt={t.scan.sections.nearby.title}
                  priority
                  className="w-full"
                />
              </div>
            </div>
          </section>

          {/* Diner value grid — 6 cards */}
          <section className="mb-14 lg:mb-16" aria-labelledby="benefits-heading">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 text-center lg:text-left">
              {t.scan.benefits.eyebrow}
            </p>
            <h2 id="benefits-heading" className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white mb-5 text-center lg:text-left">
              {t.scan.benefits.title}
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4">
              <div className="rounded-xl bg-slate-50 border border-slate-200 dark:bg-slate-800/50 dark:border-slate-700/50 p-6">
                <span className="inline-flex w-10 h-10 rounded-lg bg-blue-500/20 text-blue-400 items-center justify-center mb-2">
                  <MapPin className="w-5 h-5" strokeWidth={2} />
                </span>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">{t.scan.benefits.find.title}</h3>
                <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed opacity-80">{t.scan.benefits.find.desc}</p>
              </div>
              <div className="rounded-xl bg-slate-50 border border-slate-200 dark:bg-slate-800/50 dark:border-slate-700/50 p-6">
                <span className="inline-flex w-10 h-10 rounded-lg bg-sky-500/20 text-sky-400 items-center justify-center mb-2">
                  <Map className="w-5 h-5" strokeWidth={2} />
                </span>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">{t.scan.benefits.map.title}</h3>
                <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed opacity-80">{t.scan.benefits.map.desc}</p>
              </div>
              <div className="rounded-xl bg-slate-50 border border-slate-200 dark:bg-slate-800/50 dark:border-slate-700/50 p-6">
                <span className="inline-flex w-10 h-10 rounded-lg bg-amber-500/20 text-amber-400 items-center justify-center mb-2">
                  <UtensilsCrossed className="w-5 h-5" strokeWidth={2} />
                </span>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">{t.scan.benefits.dishes.title}</h3>
                <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed opacity-80">{t.scan.benefits.dishes.desc}</p>
              </div>
              <div className="rounded-xl bg-slate-50 border border-slate-200 dark:bg-slate-800/50 dark:border-slate-700/50 p-6">
                <span className="inline-flex w-10 h-10 rounded-lg bg-amber-500/20 text-amber-300 items-center justify-center mb-2">
                  <Star className="w-5 h-5" strokeWidth={2} />
                </span>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">{t.scan.benefits.featured.title}</h3>
                <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed opacity-80">{t.scan.benefits.featured.desc}</p>
              </div>
              <div className="rounded-xl bg-slate-50 border border-slate-200 dark:bg-slate-800/50 dark:border-slate-700/50 p-6">
                <span className="inline-flex w-10 h-10 rounded-lg bg-violet-500/20 text-violet-400 items-center justify-center mb-2">
                  <RefreshCw className="w-5 h-5" strokeWidth={2} />
                </span>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">{t.scan.benefits.latest.title}</h3>
                <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed opacity-80">{t.scan.benefits.latest.desc}</p>
              </div>
              <div className="rounded-xl bg-slate-50 border border-slate-200 dark:bg-slate-800/50 dark:border-slate-700/50 p-6">
                <span className="inline-flex w-10 h-10 rounded-lg bg-rose-500/20 text-rose-400 items-center justify-center mb-2">
                  <Heart className="w-5 h-5" strokeWidth={2} />
                </span>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">{t.scan.benefits.favourites.title}</h3>
                <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed opacity-80">{t.scan.benefits.favourites.desc}</p>
              </div>
            </div>
          </section>

          {/* Browse by neighbourhood — links to SEO landing pages */}
          <section className="mb-14 lg:mb-16" aria-labelledby="neighbourhoods-heading">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 text-center lg:text-left">
              {t.scan.neighbourhoods.eyebrow}
            </p>
            <h2 id="neighbourhoods-heading" className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white mb-1.5 text-center lg:text-left">
              {t.scan.neighbourhoods.title}
            </h2>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-5 text-center lg:text-left max-w-lg mx-auto lg:mx-0">
              {t.scan.neighbourhoods.subtitle}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5 lg:gap-3">
              {/* Barcelona parent page — trilingual */}
              <Link
                href={`/${language}/menu-del-dia-barcelona`}
                className="group flex items-center gap-2.5 rounded-xl bg-slate-50 border border-slate-200 dark:bg-slate-800/50 dark:border-slate-700/50 px-4 py-3.5 hover:bg-slate-100 dark:hover:bg-slate-700/60 hover:border-slate-300 dark:hover:border-slate-600 transition-all"
              >
                <span className="inline-flex w-8 h-8 rounded-lg bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 items-center justify-center shrink-0">
                  <Map className="w-4 h-4" strokeWidth={2} />
                </span>
                <span className="text-sm font-medium text-slate-700 dark:text-slate-200 group-hover:text-slate-900 dark:group-hover:text-white transition-colors">Barcelona</span>
              </Link>
              {/* Neighbourhood pages */}
              {[
                { slug: "menu-del-dia-barcelona-gracia", label: "Gràcia" },
                { slug: "menu-del-dia-barcelona-eixample", label: "Eixample" },
                { slug: "menu-del-dia-barcelona-sant-antoni", label: "Sant Antoni" },
                { slug: "menu-del-dia-barcelona-poblenou", label: "Poblenou" },
                { slug: "menu-del-dia-barcelona-el-raval", label: "El Raval" },
                { slug: "menu-del-dia-barcelona-gotic", label: "Gòtic" },
                { slug: "menu-del-dia-barcelona-born", label: "El Born" },
              ].map(({ slug, label }) => (
                <Link
                  key={slug}
                  href={`/${language}/${slug}`}
                  className="group flex items-center gap-2.5 rounded-xl bg-slate-50 border border-slate-200 dark:bg-slate-800/50 dark:border-slate-700/50 px-4 py-3.5 hover:bg-slate-100 dark:hover:bg-slate-700/60 hover:border-slate-300 dark:hover:border-slate-600 transition-all"
                >
                  <span className="inline-flex w-8 h-8 rounded-lg bg-blue-500/20 text-blue-600 dark:text-blue-400 items-center justify-center shrink-0">
                    <MapPin className="w-4 h-4" strokeWidth={2} />
                  </span>
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-200 group-hover:text-slate-900 dark:group-hover:text-white transition-colors">{label}</span>
                </Link>
              ))}
            </div>
          </section>

          {/* Product: menu detail — mobile: screenshot first, then copy */}
          <section className="mb-14 lg:mb-16 scroll-mt-8" aria-labelledby="section-dishes-heading">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 items-center">
              <div className="order-1 lg:order-2 flex justify-center lg:justify-start">
                <IPhoneMockup
                  src={scanScreenshots.menu}
                  alt={t.scan.sections.dishes.title}
                  className="w-full"
                  sizes="(min-width: 1024px) 360px, (min-width: 640px) 280px, 240px"
                />
              </div>
              <div className="order-2 lg:order-1 text-center lg:text-left">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">
                  {t.scan.sections.dishes.eyebrow}
                </p>
                <h2 id="section-dishes-heading" className="text-3xl lg:text-4xl font-bold text-slate-900 dark:text-white mb-2">
                  {t.scan.sections.dishes.title}
                </h2>
                <p className="text-slate-600 dark:text-slate-400 text-lg leading-relaxed max-w-md mx-auto lg:mx-0 mb-3">
                  {t.scan.sections.dishes.desc}
                </p>
                <ul className="flex flex-col space-y-3 text-base text-slate-600 dark:text-slate-400 max-w-md mx-auto lg:mx-0" aria-hidden>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-500 shrink-0" />
                    {t.scan.sections.dishes.bullet1}
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-500 shrink-0" />
                    {t.scan.sections.dishes.bullet2}
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-500 shrink-0" />
                    {t.scan.sections.dishes.bullet3}
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-500 shrink-0" />
                    {t.scan.sections.dishes.bullet4}
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Product: daily feed — mobile: screenshot first, then copy */}
          <section className="mb-14 lg:mb-16 scroll-mt-8" aria-labelledby="section-feed-heading">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 items-center">
              <div className="order-1 lg:order-1 flex justify-center lg:justify-end">
                <IPhoneMockup
                  src={scanScreenshots.home}
                  alt={t.scan.sections.feed.title}
                  className="w-full"
                  sizes="(min-width: 1024px) 360px, (min-width: 640px) 280px, 240px"
                />
              </div>
              <div className="order-2 lg:order-2 text-center lg:text-left">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">
                  {t.scan.sections.feed.eyebrow}
                </p>
                <h2 id="section-feed-heading" className="text-3xl lg:text-4xl font-bold text-slate-900 dark:text-white mb-2">
                  {t.scan.sections.feed.title}
                </h2>
                <p className="text-slate-600 dark:text-slate-400 text-lg leading-relaxed max-w-md mx-auto lg:mx-0 mb-3">
                  {t.scan.sections.feed.desc}
                </p>
                <ul className="flex flex-col space-y-3 text-base text-slate-600 dark:text-slate-400 max-w-md mx-auto lg:mx-0" aria-hidden>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-500 shrink-0" />
                    {t.scan.sections.feed.bullet1}
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-500 shrink-0" />
                    {t.scan.sections.feed.bullet2}
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-500 shrink-0" />
                    {t.scan.sections.feed.bullet3}
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-500 shrink-0" />
                    {t.scan.sections.feed.bullet4}
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Restaurant owner value */}
          <section className="mb-14 lg:mb-16" aria-labelledby="owner-heading">
            <div className="rounded-2xl overflow-hidden bg-slate-50 border border-slate-200 dark:bg-slate-800/70 dark:border-slate-700/50 shadow-xl shadow-slate-900/5 dark:shadow-black/25">
              <div className="grid lg:grid-cols-2 gap-6 lg:gap-8 p-6 sm:p-8 lg:p-9 items-center">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">
                    {t.scan.owner.eyebrow}
                  </p>
                  <h2 id="owner-heading" className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white mb-2">
                    {t.scan.owner.headline}
                  </h2>
                  <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed mb-4 max-w-lg">
                    {t.scan.owner.intro}
                  </p>
                  <ul className="space-y-2 text-sm text-slate-700 dark:text-slate-300">
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" aria-hidden />
                      {t.scan.owner.benefit1}
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" aria-hidden />
                      {t.scan.owner.benefit2}
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" aria-hidden />
                      {t.scan.owner.benefit3}
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" aria-hidden />
                      {t.scan.owner.benefit4}
                    </li>
                  </ul>
                </div>
                <div className="flex flex-col justify-center items-center lg:items-end">
                  <Store className="w-14 h-14 text-slate-400 dark:text-slate-600" aria-hidden />
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>

      <MarketingFooter locale={language} />
    </div>
  );
}
